import { useCallback, useMemo, useState } from 'react';
import { globalToastService } from '@repo/atomic-components';
import {
  ChatAccessType,
  ChatInvitedUser,
  ChatShareCandidate,
  diffChatUserAccess,
  useGetChatInvitedUsersQuery,
  useGetChatShareCandidatesQuery,
  useSetChatUserAccessMutation,
} from '@/api/chatSharing';
import { useEditChatTitleMutation } from '@/api/chat';
import { selectUserAboutData, useAppSelector } from '@/store';
import { TITLE_MAX_LENGTH } from '@/ui/features/chat/constants';
import { getPersonLabels } from '@/utils/personLabels';
import { ChatSettingsDialog } from './ChatSettingsDialog';

/** Mounted only while open, so every edit starts from the chat's persisted settings. */
export interface ChatSettingsModalProps {
  visible: boolean;
  onHide: () => void;
  chatId: string;
  chatTitle: string;
}

export const ChatSettingsModal = ({ visible, onHide, chatId, chatTitle }: ChatSettingsModalProps) => {
  const [title, setTitle] = useState(chatTitle);
  /** `null` until the user edits the access list, so server updates keep flowing through until then. */
  const [draftUsers, setDraftUsers] = useState<ChatInvitedUser[] | null>(null);

  const { data: invitedData, isFetching: isFetchingInvited } = useGetChatInvitedUsersQuery(
    { chatId },
    { skip: !visible },
  );
  const { data: candidatesData, isError: hasCandidatesError } = useGetChatShareCandidatesQuery(undefined, {
    skip: !visible,
  });
  const [setChatUserAccess, { isLoading: isSavingAccess }] = useSetChatUserAccessMutation();
  const [editChatTitle, { isLoading: isSavingTitle }] = useEditChatTitleMutation();
  const currentUserId = useAppSelector(selectUserAboutData)?.userId;

  const invitedUsers = useMemo(() => invitedData?.users ?? [], [invitedData]);
  const users = draftUsers ?? invitedUsers;

  /** The department list includes the requester, who always has access to their own chat. */
  const candidates = useMemo(
    () => (candidatesData?.candidates ?? []).filter((candidate) => candidate.userId !== currentUserId),
    [candidatesData, currentUserId],
  );

  /** A name two candidates share gets the email, in the search and in the access list alike. */
  const labelByUserId = useMemo(
    () => getPersonLabels(candidates.map(({ userId, name, email }) => ({ id: userId, name, email }))),
    [candidates],
  );
  const getUserLabel = useCallback(
    (user: { userId: string; name: string }) => labelByUserId.get(user.userId) ?? user.name,
    [labelByUserId],
  );

  const trimmedTitle = title.trim();
  const isTitleChanged = trimmedTitle !== chatTitle;
  const accessChanges = useMemo(
    () => (draftUsers === null ? [] : diffChatUserAccess(invitedUsers, draftUsers)),
    [draftUsers, invitedUsers],
  );
  const isSaving = isSavingTitle || isSavingAccess;
  const canSave = Boolean(trimmedTitle) && (isTitleChanged || accessChanges.length > 0);

  const handleHide = useCallback(() => {
    setDraftUsers(null);
    onHide();
  }, [onHide]);

  const handleAddUser = useCallback(
    (candidate: ChatShareCandidate) => {
      setDraftUsers((current) => {
        const next = current ?? invitedUsers;
        if (next.some((user) => user.userId === candidate.userId)) {
          return next;
        }
        return [...next, { ...candidate, accessType: ChatAccessType.VIEWER }];
      });
    },
    [invitedUsers],
  );

  const handleAccessTypeChange = useCallback(
    (target: ChatInvitedUser, accessType: ChatAccessType) => {
      setDraftUsers((current) =>
        (current ?? invitedUsers).map((user) => (user.userId === target.userId ? { ...user, accessType } : user)),
      );
    },
    [invitedUsers],
  );

  const handleRemoveUser = useCallback(
    (target: ChatInvitedUser) => {
      setDraftUsers((current) => (current ?? invitedUsers).filter((user) => user.userId !== target.userId));
    },
    [invitedUsers],
  );

  const handleSave = useCallback(async () => {
    try {
      if (isTitleChanged) {
        await editChatTitle({ chatId, chatTitle: trimmedTitle.slice(0, TITLE_MAX_LENGTH) }).unwrap();
      }
      if (accessChanges.length > 0) {
        await setChatUserAccess({ chatId, users: accessChanges }).unwrap();
      }
      globalToastService.show({
        closable: true,
        severity: 'success',
        detail: 'Chat settings saved.',
        life: 6000,
      });
      handleHide();
    } catch (error) {
      globalToastService.show({
        closable: true,
        severity: 'error',
        detail: 'Could not save chat settings. Please try again.',
        life: 6000,
      });
      console.error('Failed to save chat settings:', error);
    }
  }, [accessChanges, chatId, editChatTitle, handleHide, isTitleChanged, setChatUserAccess, trimmedTitle]);

  return (
    <ChatSettingsDialog
      visible={visible}
      onHide={handleHide}
      title={title}
      onTitleChange={setTitle}
      users={users}
      candidates={candidates}
      getUserLabel={getUserLabel}
      hasCandidatesError={hasCandidatesError}
      isLoading={isFetchingInvited && draftUsers === null}
      isSaving={isSaving}
      canSave={canSave}
      onAddUser={handleAddUser}
      onAccessTypeChange={handleAccessTypeChange}
      onRemoveUser={handleRemoveUser}
      onSave={handleSave}
    />
  );
};
